import React, { useState, useEffect, useRef } from 'react'
import { AxiosPromise } from 'axios'
import { NetSuiteAsset } from 'types'

export interface WithDataApiProps<T = any> {
  onLoadData?: (data: T) => T
  apiMethod: () => AxiosPromise<T>
  dependencies?: any[] // For triggering re-fetch when dependencies change
}

export interface WithDataApiState<T = any> {
  data?: T
  error: string
  isLoading: boolean
}

// Specific types for different API responses
export interface SitesData {
  sites: any[]
  totalCount: number
}

export interface NetSuiteAssetsData {
  assets: NetSuiteAsset[]
  totalCount: number
}

export interface NetSuiteCustomersData {
  customers: any[]
  totalCount: number
}

export interface ProfileData {
  profiles: any[]
  totalCount: number
}

export default function withDataApi<
  TData = any,
  TProps extends object = object,
>(WrappedComponent: React.ComponentType<TProps & WithDataApiState<TData>>) {
  return function WithDataApiComponent(
    props: TProps & WithDataApiProps<TData>,
  ) {
    const [data, setData] = useState<TData | null>(null)
    const [error, setError] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const previousDependenciesRef = useRef<any[]>([])
    const isMountedRef = useRef<boolean>(true)

    const fetchData = async () => {
      if (!props.apiMethod) {
        if (isMountedRef.current) {
          setData(null)
        }
        return
      }

      if (isMountedRef.current) {
        setData(null)
        setError('')
        setIsLoading(true)
      }

      try {
        const response = await props.apiMethod()
        if (isMountedRef.current) {
          if (response.status === 200) {
            let responseData = response.data
            if (props.onLoadData) {
              responseData = props.onLoadData(responseData)
            }
            setData(responseData)
            setError('')
          } else {
            setData(null)
            setError(response.status.toString())
          }
        }
      } catch (err) {
        if (isMountedRef.current) {
          setData(null)
          setError(err instanceof Error ? err.message : 'An error occurred')
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    useEffect(() => {
      const currentDependencies = props.dependencies || []
      const previousDependencies = previousDependenciesRef.current
      
      // Check if dependencies have actually changed
      const dependenciesChanged = currentDependencies.length !== previousDependencies.length ||
        currentDependencies.some((dep, index) => dep !== previousDependencies[index])
      
      // Only fetch if dependencies have actually changed or if this is the first render
      if (dependenciesChanged || previousDependencies.length === 0) {
        previousDependenciesRef.current = [...currentDependencies]
        fetchData()
      }
    }, [props.apiMethod, ...(props.dependencies || [])])

    // Cleanup effect to prevent state updates after unmount
    useEffect(() => {
      return () => {
        isMountedRef.current = false
      }
    }, [])

    return (
      <WrappedComponent
        {...props}
        data={data}
        error={error}
        isLoading={isLoading}
      />
    )
  }
}

// Convenience HOC for common secondstepAPI methods
export const withSitesData = withDataApi
export const withNetSuiteAssetsData = withDataApi
export const withNetSuiteCustomersData = withDataApi
export const withProfileData = withDataApi
export const withLocationData = withDataApi

// Example usage:
/*
// For sites data
const SitesComponent = withDataApi<NetSuiteAsset[]>(MyComponent)
<SitesComponent 
  apiMethod={() => ssapi.getNetSuiteAssets({ limit: 10, offset: 0 })}
  dependencies={[filters]}
/>

// For NetSuite assets with custom data transformation
const AssetsComponent = withDataApi<NetSuiteAsset[]>(MyComponent)
<AssetsComponent 
  apiMethod={() => ssapi.getNetSuiteAssets({ status: 'active' })}
  onLoadData={(data) => data.filter(asset => asset.isActive)}
/>

// For profile search with parameters
const ProfileSearchComponent = withDataApi<any[]>(MyComponent)
<ProfileSearchComponent 
  apiMethod={() => ssapi.searchProfiles({ email: 'test@example.com' })}
  dependencies={[searchTerm]}
/>
*/
